import { useRef, useState, useEffect } from 'react';
import { AnimatedCounter } from '../../shared/AnimatedCounter';
import { useKpiActiveStartupsV1 } from '@/hooks/database/useKpiActiveStartupsV1';
import { useKpiScaleupsActiveTotalV1 } from '@/hooks/database/useKpiScaleupsActiveTotalV1';
import { useKpiUnicornsActiveTotalV1 } from '@/hooks/database/useKpiUnicornsActiveTotalV1';
import { useKpiTaxGeneratedAllTimeV1 } from '@/hooks/database/useKpiTaxGeneratedAllTimeV1';
import { useKpiJobsCreatedAllTimeV1 } from '@/hooks/database/useKpiJobsCreatedAllTimeV1';
import { useKpiInvestmentsAttractedAllTimeV1 } from '@/hooks/database/useKpiInvestmentsAttractedAllTimeV1';
import { logDatabaseError } from '@/utils/errorLogger';

const StatisticsSectionEn = () => {
  const [statsVisible, setStatsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  // Fetch all KPIs from database
  const { data: kpiData, isError, error } = useKpiActiveStartupsV1();
  const { data: scaleupsData, isError: scaleupsError } = useKpiScaleupsActiveTotalV1();
  const { data: unicornsData, isError: unicornsError } = useKpiUnicornsActiveTotalV1();
  const { data: taxData, isError: taxError } = useKpiTaxGeneratedAllTimeV1();
  const { data: jobsData, isError: jobsError } = useKpiJobsCreatedAllTimeV1();
  const { data: investmentsData, isError: investmentsError } = useKpiInvestmentsAttractedAllTimeV1();

  // Use database values directly (0 if no data)
  const activeStartupsCount = kpiData?.startup_count ?? 0;
  const scaleupCount = scaleupsData?.scaleup_count ?? 0;
  const unicornCount = unicornsData?.unicorn_count ?? 0;
  const taxGenerated = taxData?.total_tax ?? 0;
  const jobsCreated = jobsData?.jobs_created ?? 0;
  const investmentsAttracted = investmentsData?.total_investment ?? 0;

  // Log errors in development mode
  useEffect(() => {
    if (isError) logDatabaseError('StatisticsSection', 'fetch active startups KPI', error);
    if (scaleupsError) logDatabaseError('StatisticsSection', 'fetch scaleups KPI', scaleupsError);
    if (unicornsError) logDatabaseError('StatisticsSection', 'fetch unicorns KPI', unicornsError);
    if (taxError) logDatabaseError('StatisticsSection', 'fetch tax KPI', taxError);
    if (jobsError) logDatabaseError('StatisticsSection', 'fetch jobs KPI', jobsError);
    if (investmentsError) logDatabaseError('StatisticsSection', 'fetch investments KPI', investmentsError);
  }, [isError, error, scaleupsError, unicornsError, taxError, jobsError, investmentsError]);

  useEffect(() => {
    const observer = new window.IntersectionObserver(([entry]) => setStatsVisible(entry?.isIntersecting ?? false), {
      threshold: 0.4
    });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);
  return <section ref={sectionRef} className="py-8" style={{
    backgroundColor: 'hsl(var(--text-black))'
  }}>
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {/* Top Row - Original 3 KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-on-scroll mb-8">
            <div className="text-center">
              <div className="mb-4">
                <div className="w-full h-1 rounded-full mb-4 stat-line-1"></div>
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={activeStartupsCount} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-4xl text-slate-100">+</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Active Startups<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
            <div className="text-center">
              <div className="mb-4">
                <div className="w-full h-1 rounded-full mb-4 stat-line-2"></div>
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={scaleupCount} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-4xl text-slate-100">+</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Scaleups<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
            <div className="text-center">
              <div className="mb-6 bg-transparent">
                <div className="w-full h-1 rounded-full mb-8 stat-line-3"></div>
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={unicornCount} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-4xl text-slate-100">+</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Unicorns<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
          </div>

          {/* Bottom Row - New 3 KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 animate-on-scroll mb-6">
            <div className="text-center">
              <div className="mb-4">
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={taxGenerated} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-3xl text-slate-100 ml-1">M€</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Tax Revenue<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
            <div className="text-center">
              <div className="mb-4">
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={jobsCreated} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-4xl text-slate-100">+</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Jobs Created<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
            <div className="text-center">
              <div className="mb-4">
                <h3 className="text-5xl lg:text-6xl mb-3">
                  <AnimatedCounter end={investmentsAttracted} trigger={statsVisible} className="text-slate-100" />
                  <sup className="text-3xl text-slate-100 ml-1">B€</sup>
                </h3>
                <p className="text-xl font-semibold text-slate-100">Total Investment<sup className="text-sm text-slate-400 ml-1">¹</sup></p>
              </div>
            </div>
          </div>

          {/* Single Footnote */}
          <div className="text-center text-slate-400 text-sm mt-8">
            <p><sup>¹</sup> Data sourced from SSN's internal database, calculated from 2015 forward</p>
          </div>
        </div>
      </div>
    </section>;
};
export default StatisticsSectionEn;
